import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { BrandLockup, PoweredByFundle } from "./Brand";
import {
  LayoutGrid, FileText, Users, Sparkles, CalendarClock, BadgeCheck,
  Database, Plug, ScrollText, LogOut, CheckSquare, Radar, ArrowLeftRight, Trophy,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutGrid, roles: ["super_admin", "group_hr_head", "entity_hr_head", "hiring_manager", "recruiter", "auditor", "business_head", "onboarding_specialist", "integration_admin", "ai_knowledge_admin"] },
  { to: "/", label: "My Portal", icon: LayoutGrid, roles: ["candidate"] },
  { to: "/approvals", label: "Approvals", icon: CheckSquare, roles: ["finance_approver", "business_head", "group_hr_head", "entity_hr_head", "hiring_manager"] },
  { to: "/requisitions", label: "Requisitions", icon: FileText, roles: ["super_admin", "group_hr_head", "entity_hr_head", "hiring_manager", "recruiter", "auditor"] },
  { to: "/sourcing", label: "Sourcing", icon: Radar, roles: ["super_admin", "group_hr_head", "entity_hr_head", "hiring_manager", "recruiter", "integration_admin"] },
  { to: "/candidates", label: "Candidates", icon: Users, roles: ["super_admin", "group_hr_head", "entity_hr_head", "hiring_manager", "recruiter", "onboarding_specialist", "auditor", "employee_referrer"] },
  { to: "/shortlist", label: "Ranked Shortlist", icon: Trophy, roles: ["super_admin", "group_hr_head", "entity_hr_head", "hiring_manager", "recruiter"] },
  { to: "/agent", label: "AI Assistant", icon: Sparkles, roles: "all" },
  { to: "/interviews", label: "Interviews", icon: CalendarClock, roles: ["super_admin", "group_hr_head", "entity_hr_head", "hiring_manager", "recruiter", "auditor"] },
  { to: "/", label: "My Interviews", icon: CalendarClock, roles: ["interview_panelist"] },
  { to: "/offers", label: "Offers & Onboarding", icon: BadgeCheck, roles: ["super_admin", "group_hr_head", "entity_hr_head", "hiring_manager", "recruiter", "onboarding_specialist", "auditor"] },
  { to: "/master-data", label: "Master Data", icon: Database, roles: ["super_admin", "group_hr_head", "entity_hr_head"] },
  { to: "/integrations", label: "Integrations", icon: Plug, roles: ["super_admin", "group_hr_head", "integration_admin"] },
  { to: "/hrone", label: "HROne Sync", icon: ArrowLeftRight, roles: ["super_admin", "group_hr_head", "entity_hr_head", "integration_admin", "recruiter"] },
  { to: "/audit", label: "Audit Log", icon: ScrollText, roles: ["super_admin", "group_hr_head", "auditor"] },
];

const ROLE_LABELS = {
  super_admin: "Super Admin", group_hr_head: "Group HR Head", entity_hr_head: "Entity HR Head",
  hiring_manager: "Hiring Manager", recruiter: "Recruiter", interview_panelist: "Interview Panelist",
  candidate: "Candidate", ai_knowledge_admin: "AI Knowledge Admin", finance_approver: "Finance Approver",
  business_head: "Business Head", onboarding_specialist: "Onboarding Specialist", auditor: "Auditor",
  integration_admin: "Integration Admin", employee_referrer: "Employee Referrer",
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = NAV.filter((n) => n.roles === "all" || n.roles.includes(user?.role));

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-64 shrink-0 border-r bg-[#0A251D] flex flex-col fixed inset-y-0 z-30" data-testid="sidebar">
        <div className="px-5 py-6 border-b border-white/10">
          <BrandLockup light />
        </div>
        <nav className="flex-1 overflow-y-auto py-4">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 mx-3 my-0.5 px-3.5 py-2.5 text-sm rounded-lg transition-colors duration-150 ${
                  isActive
                    ? "bg-white/10 text-white font-semibold"
                    : "text-[#A8C3B4] hover:text-white hover:bg-white/5"
                }`
              }
            >
              <n.icon size={17} strokeWidth={2} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 px-5 py-4">
          <div className="text-sm font-semibold truncate text-white" data-testid="current-user-name">{user?.name}</div>
          <div className="text-xs text-[#A8C3B4]" data-testid="current-user-role">{ROLE_LABELS[user?.role] || user?.role}</div>
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="mt-3 w-full flex items-center justify-center gap-2 text-xs uppercase tracking-widest font-bold border border-white/20 text-[#C9DCD0] py-2 rounded-lg hover:bg-white hover:text-[#0A251D] transition-colors duration-150"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 ml-64 min-w-0">
        <div className="px-8 py-8 max-w-[1440px]">{children}</div>
        <footer className="px-8 pb-6">
          <PoweredByFundle />
        </footer>
      </main>
    </div>
  );
}
